import { cn } from "@/lib/utils"
import { Stepper } from "@/components/ui-kit/stepper"
import { Tabs, TabsContent } from "@/components/ui/tabs"
import { RentStepButtons } from "@/components/rent/rent-step-buttons"
import { WarehouseStep } from "@/components/rent/warehouse-step"
import { BoxStep } from "@/components/rent/box-step"
import { PersonalStep } from "@/components/rent/personal-step"
import { PaymentStep } from "@/components/rent/payment-step"
import { ContractsPayDialog } from "@/components/contracts/modals/contracts-pay-dialog"
import { useRentFlow } from "@/hooks/rent/useRentFlow"
import { useIsMobile } from "@/hooks/useIsMobile"

export function RentPage() {
  const isMobile = useIsMobile()
  const rent = useRentFlow()

  const stepButtonsProps = {
    isLastStep: rent.isLastStep,
    isNextDisabled: rent.isNextDisabled,
    onNext: rent.handleNext,
    onBack: rent.handleBack,
  }

  return (
    <div className={cn("flex flex-col bg-background", isMobile ? "h-dvh overflow-hidden" : "h-dvh")}>
      <Stepper
        tabs={rent.tabs}
        activeStep={rent.activeStep}
        onStepChange={rent.handleStepChange}
      />

      <main
        className={cn(
          "mx-auto flex w-full min-w-0 max-w-[1920px] grow overflow-x-hidden",
          isMobile && "min-h-0 overflow-hidden pb-16",
        )}
      >
        <section className={cn("m-2 min-h-0 min-w-0 grow rounded-[8px] lg:m-4", isMobile && "flex flex-col overflow-hidden")}>
          <Tabs
            value={rent.activeStep}
            onValueChange={rent.handleStepChange}
            className="h-full min-h-0 min-w-0"
          >
            <TabsContent
              value="warehouse"
              className={cn("h-full min-h-0", isMobile && "overflow-hidden")}
            >
              <WarehouseStep
                isMobile={isMobile}
                facilities={rent.availableFacilities}
                isFacilitiesLoading={rent.isFacilitiesLoading}
                selectedFacilityCode={rent.selectedFacilityCode}
                selectedFacility={rent.selectedFacility}
                onFacilitySelect={rent.handleFacilitySelect}
                footer={
                  !isMobile ? <RentStepButtons {...stepButtonsProps} isFirstStep /> : undefined
                }
              />
            </TabsContent>

            <TabsContent
              value="box"
              className={cn("h-full min-h-0", isMobile && "overflow-y-auto overscroll-contain")}
            >
              <BoxStep
                isActive={rent.activeStep === "box"}
                isMobile={isMobile}
                facilityCode={rent.selectedFacilityCode}
                unitTypes={rent.selectedFacility?.unitTypes ?? []}
                occupiedUnitTypes={rent.selectedFacility?.occupiedUnitTypes ?? []}
                selectedUnitTypeCode={rent.selectedUnitTypeCode}
                onUnitTypeChange={rent.setSelectedUnitTypeCode}
                selectedUnitType={rent.selectedUnitType}
                capabilities={rent.selectedFacilityCapabilities}
                unitTypeDetailRef={rent.unitTypeDetailRef}
                footer={!isMobile ? <RentStepButtons {...stepButtonsProps} /> : undefined}
              />
            </TabsContent>

            <TabsContent
              value="personal"
              className={cn("h-full min-h-0", isMobile && "overflow-y-auto overscroll-contain")}
            >
              <PersonalStep
                isMobile={isMobile}
                personalPhase={rent.personalPhase}
                personalSideTitle={rent.personalSideTitle}
                customers={rent.customers}
                selectedCustomerId={rent.selectedCustomerId}
                onCustomerChange={rent.setSelectedCustomerId}
                onPersonalTabChange={rent.setPersonalTab}
                personalFormRef={rent.personalFormRef}
                customerSelectRef={rent.customerSelectRef}
                onLoginSubmit={rent.handleNext}
                footer={!isMobile ? <RentStepButtons {...stepButtonsProps} /> : undefined}
              />
            </TabsContent>

            <TabsContent
              value="payment"
              className={cn("h-full min-w-0", isMobile && "min-h-0 overflow-y-auto overscroll-contain")}
            >
              <PaymentStep
                isActive={rent.activeStep === "payment"}
                isMobile={isMobile}
                startedAt={rent.startedAt}
                onStartedAtChange={rent.setStartedAt}
                period={rent.period}
                onPeriodChange={rent.setPeriod}
                paymentPlanOptions={rent.paymentPlanOptions}
                isPricingLoading={rent.isPricingLoading}
                planErrorMessage={rent.planErrorMessage}
                promoDraft={rent.promoDraft}
                onPromoDraftChange={rent.setPromoDraft}
                onApplyPromo={rent.handleApplyPromo}
                appliedPromo={rent.appliedPromo}
                promoErrorMessage={rent.promoErrorMessage}
                isRecurrent={rent.isRecurrent}
                onRecurrentChange={rent.setIsRecurrent}
                selectedFacility={rent.selectedFacility?.facility}
                selectedUnitType={rent.selectedUnitType}
                selectedPrice={rent.selectedPrice}
                paymentError={rent.paymentError}
                isSubmitting={rent.isPaymentSubmitting}
                isPromoApplying={rent.isPromoApplying}
                footer={!isMobile ? <RentStepButtons {...stepButtonsProps} /> : undefined}
              />
            </TabsContent>
          </Tabs>
        </section>
      </main>

      <ContractsPayDialog
        open={rent.isPayDialogOpen}
        onOpenChange={rent.handlePayDialogOpenChange}
        invoices={rent.invoicesToPay}
        contractId={rent.createdContract?.id}
        isRecurrent={rent.isRecurrent}
      />

      {isMobile && (
        <RentStepButtons
          {...stepButtonsProps}
          isFirstStep={rent.activeStep === "warehouse"}
          fixed
        />
      )}
    </div>
  )
}
